import React from 'react';
import classNames from 'classnames';
import { translationProvider } from 'twain-sport-fe-mono/store/translations';
import { replaceAll } from 'twain-sport-fe-mono/utils/replaceAll';
import {
    getTechLossReasonTranslation,
    getTieReasonTranslation,
} from 'twain-sport-fe-mono/utils/getResultReasonTranslation';
import {
    ILeftVsRight,
    IStandingsParticipant,
    IStandingsMatch,
} from 'twain-sport-fe-mono/interfaces';
import { ResultType, MatchStatus } from 'twain-sport-fe-mono/enums';
import { InfoIcon } from 'twain-sport-fe-mono/icons';
import { EMPTY_SPACE } from 'twain-sport-fe-mono/constants/textSymbols';
import { useStandingsContext } from 'twain-sport-fe-mono/context';
import { Tooltip, TooltipPosition } from '../Tooltip';
import {
    StandingsMatchParticipant,
    StandingsMatchParticipantFull,
} from './StandingsMatchParticipant';
import { StandingsMatchStatus } from './StandingsMatchStatus';
import classes from './StandingsMatch.module.scss';

interface IProps {
    participants: ILeftVsRight<IStandingsParticipant>;
    match: IStandingsMatch;
    className?: string;
    fullInfo?: boolean;
}

const getTieWinnerName = (
    participants: ILeftVsRight<IStandingsParticipant>,
    match: IStandingsMatch,
): string => {
    if (match.participants.left.result === ResultType.WON) {
        return participants.left.nickname;
    }

    if (match.participants.right.result === ResultType.WON) {
        return participants.right.nickname;
    }

    return '';
};

const defaultProps: Partial<IProps> = {
    fullInfo: false,
};

export const StandingsMatch: React.FC<IProps> = (props) => {
    const { renderMatchStatus: RenderMatchStatus } = useStandingsContext();
    const participantInfoLeft = props.participants.left;
    const participantInfoRight = props.participants.right;
    const participantLeft = props.match.participants.left;
    const participantRight = props.match.participants.right;

    const ParticipantComponent = props.fullInfo
        ? StandingsMatchParticipantFull
        : StandingsMatchParticipant;

    const renderTooltip = () => {
        let tooltipContent: React.ReactNode;
        let isSmallContent = false;

        if (props.match.status === MatchStatus.HELD) {
            tooltipContent = (
                <div className={classes.textSmall}>
                    {translationProvider.string('match_results_under_review')}
                </div>
            );
        } else if (
            (props.match.status === MatchStatus.OPENED ||
                props.match.status === MatchStatus.CREATED) &&
            props.match.wasPostponed
        ) {
            isSmallContent = true;
            tooltipContent = (
                <div className={classes.textSmall}>
                    {translationProvider.string('match_postponed')}
                </div>
            );
        } else if (props.match.resultType === ResultType.TECH_LOSS) {
            isSmallContent = true;
            tooltipContent = (
                <div className={classes.textSmall}>
                    {translationProvider.string(
                        getTechLossReasonTranslation(props.match.resultReason),
                    )}
                </div>
            );
        } else if (props.match.resultType === ResultType.TIE) {
            tooltipContent = (
                <>
                    <div className={classNames(classes.textMain, classes.textLong)}>
                        {replaceAll(
                            translationProvider.string('{{participant}}_wins_by_{{reason}}'),
                            {
                                participant: getTieWinnerName(props.participants, props.match),
                                reason: translationProvider.string(
                                    getTieReasonTranslation(props.match.resultReason),
                                ),
                            },
                        )}
                    </div>
                    <div className={classes.textSmall}>
                        {translationProvider.string('accuracy')}:{EMPTY_SPACE}
                        {`${participantLeft.accuracy}% - ${participantRight.accuracy}`}%
                    </div>
                    <div className={classes.textSmall}>
                        {translationProvider.string('ranking')}:{EMPTY_SPACE}
                        {`${participantLeft.rank} - ${participantRight.rank}`}
                    </div>
                </>
            );
        }

        if (!tooltipContent) {
            return null;
        }

        return (
            <div className={classes.infoIconWrapper} role="button" tabIndex={0}>
                <Tooltip
                    position={TooltipPosition.LEFT}
                    className={classNames(classes.tooltip, { [classes.small]: isSmallContent })}
                >
                    <div className={classes.tooltipContent}>{tooltipContent}</div>
                </Tooltip>
                <InfoIcon className={classes.icon} />
            </div>
        );
    };

    return (
        <div className={classNames(classes.container, props.className)}>
            <div className={classes.participants}>
                <ParticipantComponent
                    participantInfo={participantInfoLeft}
                    participant={participantLeft}
                />
                <ParticipantComponent
                    participantInfo={participantInfoRight}
                    participant={participantRight}
                />
                {renderTooltip()}
            </div>
            {RenderMatchStatus ? (
                <RenderMatchStatus match={props.match} />
            ) : (
                <StandingsMatchStatus match={props.match} />
            )}
        </div>
    );
};

StandingsMatch.defaultProps = defaultProps;
